import { useRef } from "react";

import { cn } from "../cn";
import type { TabDefinition } from "./TabSwitcher";

interface SectionTabsProps<Id extends string> {
    tabs: TabDefinition<Id>[];
    activeTab: Id;
    onChange: (id: Id) => void;
    /** Accessible name for the tab list, e.g. "Scheduler settings". */
    label: string;
    /** Overrides the default spacing, e.g. inside a flex column with its own gap. */
    className?: string;
}

/**
 * Underlined tabs for the sections inside one page or panel.
 *
 * Lighter than `TabSwitcher` on purpose: it sits under a page's own tabs
 * (practice settings → Scheduler → Hours) or its main filter (Meditations →
 * Mine → Tracks), and two segmented controls stacked read as two equal
 * choices rather than a choice and its sub-choice.
 */
export default function SectionTabs<Id extends string>({
    tabs,
    activeTab,
    onChange,
    label,
    className,
}: SectionTabsProps<Id>) {
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    /** Arrow keys move between tabs and wrap, per the ARIA tabs pattern. */
    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        const offset =
            event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
        if (offset === 0) return;

        event.preventDefault();
        const next = tabs[(index + offset + tabs.length) % tabs.length];
        onChange(next.id);
        tabRefs.current[next.id]?.focus();
    };

    return (
        <div
            role="tablist"
            aria-label={label}
            className={cn(
                "no-scrollbar mb-5 flex gap-1 overflow-x-auto border-b border-border-subtle",
                className
            )}
        >
            {tabs.map((tab, index) => {
                const isActive = tab.id === activeTab;
                return (
                    <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        ref={(node) => {
                            tabRefs.current[tab.id] = node;
                        }}
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        onClick={() => onChange(tab.id)}
                        className={cn(
                            "-mb-px flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors",
                            isActive
                                ? "border-accent text-content-primary"
                                : "border-transparent text-content-secondary hover:text-content-primary"
                        )}
                    >
                        <tab.icon size={15} aria-hidden="true" />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
}
